import React, { useMemo, MouseEvent } from 'react';
import cn from 'classnames';
import {
  HeaderWrapper,
  HeaderRow,
  BodyWrapper,
  dataTableStyles,
} from '@dealroadshow/uikit/core/components/Table/DataTable';
import SectorRow from '@/finsight/ui/components/deals/DealsList/DealBody/Tranches/Sector/SectorRow';
import IndustryRow from '@/finsight/ui/components/deals/DealsList/DealBody/Tranches/Industry/IndustryRow';
import getSectorColumns from '@/finsight/ui/components/deals/DealsList/DealBody/Tranches/Sector/SectorColumns';
import getIndustryColumns from '@/finsight/ui/components/deals/DealsList/DealBody/Tranches/Industry/IndustryColumns';
import CheckUserPlanAccess from '@/finsight/ui/common/components/CheckUserPlanAccess';
import { RestrictedAccessExplanation } from '@/finsight/ui/common/components/Explanations';
import { useDealBlur } from '@/finsight/ui/components/deals/useDealBlur';
import { useDealTooltipContext } from '@/finsight/application/Deal/DealTooltipContext';
import {
  usePopoverModifier,
} from '@/finsight/ui/common/components/Explanations/RestrictedAccessExplanation/usePopoverModifier';
import { PRICING_EXPLAINATION_TITLE, PRICING_EXPLAINATION_DESCRIPTION } from '@/finsight/ui/components/deals/constants';
import { DealType } from '@/finsight/domain/vo/IDeal';
import type { IRating } from '@/finsight/domain/vo/IRating';
import type ITranch from '@/finsight/domain/vo/Tranch/ITranch';

import styles from './trancheTable.scss';

interface ITrancheTableProps {
  dealType: DealType,
  tranches: ITranch[],
  isDealCmbs: boolean,
  ratingsMapping: Record<string, IRating[]>,
  dealProductId?: string,
}

const TrancheTable = ({
  dealType,
  tranches,
  isDealCmbs,
  ratingsMapping,
  dealProductId,
}: ITrancheTableProps) => {
  const {
    dealTooltipOpened,
    setDealTooltipOpened,
  } = useDealTooltipContext();
  const {
    isBlurred,
    planWithoutBlur,
  } = useDealBlur();
  const {
    popperModifiers,
    setTooltipOffset,
    getTooltipOffset,
  } = usePopoverModifier();

  const columns = useMemo(
    () => {
      if (dealType === DealType.SECTOR) {
        // @ts-ignore
        return getSectorColumns(isDealCmbs, ratingsMapping);
      }
      if (dealType === DealType.INDUSTRY) {
        return getIndustryColumns(ratingsMapping);
      }

      return undefined;
    },
    [tranches[0].id],
  );

  const RowComponent = useMemo(
    () => (dealType === DealType.SECTOR ? SectorRow : IndustryRow),
    [tranches[0].id],
  );

  const onClick = (event: MouseEvent) => {
    const offset = getTooltipOffset(event);
    event.stopPropagation();
    setTooltipOffset(offset);

    if (dealTooltipOpened !== dealType) {
      setDealTooltipOpened(dealType);
    }
  };

  const trancheTable = (
    <div className={ cn(styles.tableWrapper, { [styles.blurred]: isBlurred }) } onClick={ onClick }>
      <table
        className={ cn(dataTableStyles.dataTable, dataTableStyles.isStripedOdd) }
        data-test="dealItemTrancheTable"
      >
        <HeaderWrapper>
          <HeaderRow columns={ columns } />
        </HeaderWrapper>
        <BodyWrapper>
          {
            tranches.map((tranche) => (
              <RowComponent
                key={ tranche.id }
                tranche={ tranche }
                isDealCmbs={ isDealCmbs }
                // @ts-ignore
                ratingsMapping={ ratingsMapping }
                dealProductId={ dealProductId }
              />
            ))
          }
        </BodyWrapper>
      </table>
    </div>
  );

  return (
    <CheckUserPlanAccess
      requiredUserPlan={ planWithoutBlur }
      fallback={ (
        <RestrictedAccessExplanation
          title={ PRICING_EXPLAINATION_TITLE }
          description={ PRICING_EXPLAINATION_DESCRIPTION }
          isOpen={ dealTooltipOpened === dealType }
          closeTooltip={ () => setDealTooltipOpened(null) }
          popperModifiers={ popperModifiers }
        >
          { trancheTable }
        </RestrictedAccessExplanation>
      ) }
    >
      { trancheTable }
    </CheckUserPlanAccess>
  );
};

export default React.memo(TrancheTable);
